import { DndContext, KeyboardSensor, PointerSensor, pointerWithin, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { ArrowRight, CornerUpLeft, Info, Plus } from 'lucide-react'
import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { PROJECT_WORK_PERM } from '../../permissions'
import { useMoveTask } from '../../taskApi'
import { applyTaskFilters, type TaskFilters } from '../../taskFilters'
import { TASK_STATUSES, type Project, type Task, type TaskStatus } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { TASK_STATUS_COLUMN, TASK_STATUS_DOT } from '../../visuals'
import { TaskCard } from '../task/TaskCard'
import { TaskFormModal, type TaskDefaults } from '../task/TaskFormModal'
import { useTaskDelete } from '../task/useTaskDelete'

type Props = { project: Project; filters: TaskFilters; onNewTask: (d?: TaskDefaults) => void }

function Column({ status, count, children }: { status: TaskStatus; count: number; children: ReactNode }) {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({ id: status })
  return (
    <section
      ref={setNodeRef}
      aria-label={t(`projects.taskStatus.${status}`)}
      className={cn('flex w-[17.5rem] shrink-0 snap-start flex-col rounded-xl border p-2 transition-colors sm:w-72', TASK_STATUS_COLUMN[status], isOver && 'ring-2 ring-primary/40')}
    >
      <header className="flex items-center gap-2 px-1.5 pb-2 pt-1">
        <span className={cn('size-2.5 rounded-full', TASK_STATUS_DOT[status])} aria-hidden />
        <h3 className="text-sm font-semibold">{t(`projects.taskStatus.${status}`)}</h3>
        <span className="ml-auto rounded-full bg-card px-2 text-xs font-semibold tabular-nums text-muted-foreground shadow-xs">{count}</span>
      </header>
      <div className="flex min-h-24 flex-1 flex-col gap-2">{children}</div>
    </section>
  )
}

export function KanbanTab({ project, filters, onNewTask }: Props) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { nameOf } = useStaffLookup()
  const move = useMoveTask(project.id)
  const onDelete = useTaskDelete(project)
  const [editing, setEditing] = useState<Task | null>(null)
  const canWrite = can(PROJECT_WORK_PERM)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor))

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(TASK_STATUSES.map((s) => [s, [] as Task[]])) as Record<TaskStatus, Task[]>
    for (const task of applyTaskFilters(project.tasks, filters)) map[task.status].push(task)
    return map
  }, [project.tasks, filters])
  const customerOf = useMemo(() => new Map(project.installations.map((i) => [i.id, i.customer])), [project.installations])
  const attachmentsOf = (id: string) => project.files.filter((f) => f.taskId === id).length

  const onMove = (task: Task, status: TaskStatus) => {
    if (task.status === status) return
    move.mutate({ taskId: task.id, status }, { onError: toastError })
  }

  const onDragEnd = (e: DragEndEvent) => {
    const task = project.tasks.find((x) => x.id === e.active.id)
    if (task && e.over) onMove(task, e.over.id as TaskStatus)
  }

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={onDragEnd}>
        <div className="scrollbar-none -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-3 sm:mx-0 sm:px-0">
          {TASK_STATUSES.map((status) => (
            <Column key={status} status={status} count={byStatus[status].length}>
              {byStatus[status].map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  projectId={project.id}
                  customer={task.installationId ? customerOf.get(task.installationId) : undefined}
                  attachments={attachmentsOf(task.id)}
                  assignee={task.assigneeId ? nameOf(task.assigneeId) : null}
                  canWrite={canWrite}
                  onEdit={setEditing}
                  onDelete={(x) => void onDelete(x)}
                  onMove={onMove}
                />
              ))}
              {byStatus[status].length === 0 ? <p className="px-2 py-6 text-center text-xs text-muted-foreground">{t('projects.kanban.emptyColumn')}</p> : null}
              {canWrite && status === 'todo' ? (
                <button
                  type="button"
                  onClick={() => onNewTask({ status })}
                  className="flex items-center justify-center gap-2 rounded-xl border border-dashed bg-card/60 py-3 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Plus className="size-4" aria-hidden />
                  {t('projects.tasks.add')}
                </button>
              ) : null}
            </Column>
          ))}
        </div>
      </DndContext>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border bg-card px-3 py-2 text-xs text-muted-foreground shadow-card">
        <Info className="size-3.5" aria-hidden />
        <span className="font-medium text-foreground">{t('projects.kanban.workflow')}</span>
        {TASK_STATUSES.map((s, i) => (
          <Fragment key={s}>
            {i > 0 ? <ArrowRight className="size-3" aria-hidden /> : null}
            <span className="inline-flex items-center gap-1.5">
              <span className={cn('size-2 rounded-full', TASK_STATUS_DOT[s])} aria-hidden />
              {t(`projects.taskStatus.${s}`)}
            </span>
          </Fragment>
        ))}
        <span className="inline-flex items-center gap-1.5 sm:ml-3">
          <CornerUpLeft className="size-3.5 text-destructive" aria-hidden />
          {t('projects.kanban.rejectHint')}
        </span>
      </div>

      <TaskFormModal open={!!editing} project={project} task={editing} onOpenChange={(v) => !v && setEditing(null)} />
    </div>
  )
}
